import React from 'react';
import styled from 'styled-components';
import {FormattedMessage} from 'react-intl';
import InputSelectFieldWithValidation from '../../../../../common/InputSelectFieldWithValidation';
import {TextareaFieldWithValidation} from '../../../../../common/TextareaFieldWithValidation';
import {
    FORM_FIELD_AGREEMENT,
    FORM_FIELD_AUTHORITY_GIVEN,
    FORM_FIELD_AUTHORITY_NUMBER,
    FORM_FIELD_COURTESY_REPLY_REMARKS,
    FORM_FIELD_APPLICATION_FEES,
    FORM_FIELD_DAILY_RATE,
    FORM_FIELD_FROM,
    FORM_FIELD_UNTIL,
    FORM_FIELD_FROM_DECREASING,
    FORM_FIELD_UNTIL_DECREASING,
    FORM_FIELD_DAILY_RATE_DECREASING,
    FORM_LABEL_AGREEMENT,
    FORM_LABEL_AUTHORITY_GIVEN,
    FORM_LABEL_AUTHORITY_NUMBER,
    FORM_LABEL_COURTESY_REPLY_REMARKS,
    FORM_LABEL_APPLICATION_FEES,
    FORM_LABEL_DAILY_RATE,
    FORM_LABEL_FROM,
    FORM_LABEL_TOTAL_AMOUNT,
    FORM_LABEL_TOTAL_DAYS,
    FORM_LABEL_UNTIL, FORM_TICKET_SECTION,
    FORM_WAREHOUSE_SECTION,
    FORM_LOAN_EXTENSION_SECTION,
    FORM_CAR_REQUEST_SECTION,
    FORM_CAR_REQUEST,
    FORM_LABEL_DAILY_RATE_DECREASING,
    FORM_LABEL_UNTIL_DECREASING,
    FORM_LABEL_FROM_DECREASING,
    FORM_LABEL_DECREASING_RATE
} from '../../../../../../constants/formConstants';
import {InputFieldWithValidation} from '../../../../../common/InputFieldWithValidation';
import {Field, formValueSelector, change} from 'redux-form';
import DatePickerComponent from '../../../../../common/datePicker/DatePickerComponent';
import moment from 'moment';
import {required, positiveNumberWithZero} from '../../../../../common/validationCommons';
import {connect} from 'react-redux';
import ValidFormattedMessage from '../../../../../common/ValidFormattedMessage';
import commonMessages from '../../../../../../intl/common/commonMessages';
import formMessages from '../../../../../../intl/sparePartsOrders/formMessages';
import {countryCurrency} from '../../../../../../constants/Utils';
import get from 'get-value';
import { useMemo, useEffect, useRef } from 'react';
import { last, isEmpty } from 'lodash';

const StyledDiv = styled.div`
  border-radius: 5px;
`;

const WarehouseSection = (props) => {
    const {readOnly, userRights, authorityGiven, dailyRateLevel, dailyRateLevelDecreasing, startDate, endDate, startDateDecreasing, endDateDecreasing, reqFields, group, loanExtension, coutesyCarsPricing = [], carRequests, form, change, section, warehousePriceRef, warehouseDaysRef, applicationFees, decreasingRate, decreasingRateExtension, warehouseUntilDecreasing, warehouseUntil} = props;

    useEffect(() => {
        if (authorityGiven === 'NO') return;
        
        if (loanExtension && (warehouseUntilDecreasing || warehouseUntil)) {
            change(`${section}.${FORM_FIELD_FROM}`, moment(warehouseUntilDecreasing || warehouseUntil).add(1, 'day').format('YYYY-MM-DD'));
        }
        
        if (endDate && decreasingRate && !loanExtension) {
            change(`${section}.${FORM_FIELD_FROM_DECREASING}`, moment(endDate).add(1, 'day').format('YYYY-MM-DD'));
        }
        
        if (endDate && decreasingRateExtension && loanExtension) {
            change(`${section}.${FORM_FIELD_FROM_DECREASING}`, moment(endDate).add(1, 'day').format('YYYY-MM-DD'));
        }
    }, [endDate, decreasingRate, decreasingRateExtension, loanExtension, warehouseUntilDecreasing, warehouseUntil, change, section, authorityGiven]);

    
    const lastDealerCarRequest = useMemo(() => {
        if (!isEmpty(carRequests)) {
            return last(carRequests.filter(request => request.isDealer));
        }
        return null;
    }, [carRequests]);

    const currency = useMemo(() => get(countryCurrency, group, 'EUR'), [group]);
    
    const rateKey = useMemo(() => currency === 'EUR' ? 'dailyRate' : 'dailyRateLocal', [currency]);
    
    const dailyPrice = useMemo(() => {
        if (!dailyRateLevel) return 0;
        
        // Object with originalRate
        if (typeof dailyRateLevel === 'object') {
            return dailyRateLevel.originalRate || 0;
        }
        
        // Try to parse as number, otherwise lookup in pricing table
        const numericValue = parseFloat(dailyRateLevel);
        if (!isNaN(numericValue)) {
            return numericValue;
        }
        
        // Level key lookup
        const pricingObj = coutesyCarsPricing && coutesyCarsPricing[dailyRateLevel];
        return pricingObj ? (pricingObj[rateKey] || 0) : 0;
    }, [coutesyCarsPricing, dailyRateLevel, rateKey]);

    const dailyPriceDecreasing = useMemo(() => {
        if (!dailyRateLevelDecreasing) return 0;
        
        // Object with originalRate
        if (typeof dailyRateLevelDecreasing === 'object') {
            return dailyRateLevelDecreasing.originalRate || 0;
        }
        
        // Try to parse as number, otherwise lookup in pricing table
        const numericValue = parseFloat(dailyRateLevelDecreasing);
        if (!isNaN(numericValue)) {
            return numericValue;
        }
        
        // Level key lookup
        const pricingObj = coutesyCarsPricing && coutesyCarsPricing[dailyRateLevelDecreasing];
        return pricingObj ? (pricingObj[rateKey] || 0) : 0;
    }, [coutesyCarsPricing, dailyRateLevelDecreasing, rateKey]);

    const dailyRateOptions = useMemo(() => {
        const dailyRateOptions = [];

        Object.entries(coutesyCarsPricing).forEach(([key, value]) => {
            if (key.includes('level') && value[rateKey]) {
                dailyRateOptions.push({
                    value: key,
                    label: `Level ${key.replace('level', '')} (${value[rateKey]} ${currency})`,
                    originalRate: Number(value[rateKey]), // in case of rate level change in the future, we have to keep the original rate selected. For example, if user selects level 2 - 20 eur and later on, level 2 rate changes to 25 eur, we should keep the selected rate as 20 eur for the price calculation
                });
            }
        })

        return dailyRateOptions;
    }, [coutesyCarsPricing, rateKey, currency]);

    const numberOfDays = useMemo(() => {
        return (startDate && endDate) ? moment(endDate).diff(moment(startDate), 'days') + 1 : 0;
    }, [startDate, endDate]);

    const numberOfDaysDecreasing = useMemo(() => {
        return (startDateDecreasing && endDateDecreasing) ? moment(endDateDecreasing).diff(moment(startDateDecreasing), 'days') + 1 : 0;
    }, [startDateDecreasing, endDateDecreasing]);

    const price = useMemo(() => {
        let totalPrice = dailyPrice * numberOfDays + dailyPriceDecreasing * numberOfDaysDecreasing;
        const appFees = parseFloat(applicationFees) || 0;

        // Add application fees to total
        totalPrice = totalPrice + appFees;

        // If not loan extension, update the refs for use in loan extension section
        if (!loanExtension) {
            warehousePriceRef.current = totalPrice;
            warehouseDaysRef.current = numberOfDays + numberOfDaysDecreasing;
        }

        return totalPrice > 0 ? Math.round((totalPrice + (loanExtension ? warehousePriceRef.current : 0) + Number.EPSILON) * 100) / 100 + '\u00A0' + currency : (loanExtension ? warehousePriceRef.current : 0);
    }, [dailyPrice, numberOfDays, dailyPriceDecreasing, numberOfDaysDecreasing, currency, applicationFees, loanExtension, warehousePriceRef, warehouseDaysRef]);

    const handleAuthorityGivenChange = (e, newValue) => {
        if (newValue === 'NO') {
            // Clear all dependent fields when authority is not given
            change(`${section}.${FORM_FIELD_AUTHORITY_NUMBER}`, "");
            change(`${section}.${FORM_FIELD_AGREEMENT}`, false);
            change(`${section}.${FORM_FIELD_FROM}`, "");
            change(`${section}.${FORM_FIELD_UNTIL}`, "");
            change(`${section}.${FORM_FIELD_DAILY_RATE}`, null);
            change(`${section}.${FORM_FIELD_COURTESY_REPLY_REMARKS}`, "");
            change(`${section}.${FORM_FIELD_APPLICATION_FEES}`, "");
            handleDecreasingRateChange(false, loanExtension);
        }
    };

    const handleDecreasingRateChange = (checked, isExtension) => {
        const fieldName = isExtension ? 'decreasingRateExtension' : 'decreasingRate';
        change(`${section}.${fieldName}`, checked);
        if (!checked) {
            change(`${section}.${FORM_FIELD_FROM_DECREASING}`, "");
            change(`${section}.${FORM_FIELD_UNTIL_DECREASING}`, "");
            change(`${section}.${FORM_FIELD_DAILY_RATE_DECREASING}`, null);
        }
    };

    const isAuthorityGivenDisabled = !userRights.canEditAuthReply || readOnly || (lastDealerCarRequest && !loanExtension && lastDealerCarRequest.loanExtension);
    const isRequired = authorityGiven === 'YES' && !readOnly && userRights.canEditAuthReply;
    
    return (
        <>
            {userRights.canViewAuthReply &&
            <div className="pt-4">
                <h3 className="text-left ml-4">
                    <FormattedMessage {...(loanExtension ? formMessages.LOAN_EXTENSION_HEADER : formMessages.COURTESY_REPLY_HEADER)}/>
                </h3>
                <StyledDiv className="border border-secondary px-4 py-2 bg-white">
                    <div className="form-row">
                        <div className="col-xl-3 col-lg-4 col-md-6 pb-2">
                            <InputSelectFieldWithValidation label={formMessages[FORM_LABEL_AUTHORITY_GIVEN]}
                                                            field={FORM_FIELD_AUTHORITY_GIVEN}
                                                            options={['NO', 'YES', 'IN_TREATMENT']}
                                                            intlMessages={commonMessages}
                                                            sort={false}
                                                            handleChange={handleAuthorityGivenChange}
                                                            isDisabled={isAuthorityGivenDisabled}
                                                            isRequired={(lastDealerCarRequest && (lastDealerCarRequest.carRequest === 'YES' || lastDealerCarRequest.loanExtension === 'YES')) && !isAuthorityGivenDisabled}/>
                        </div>
                        <div className="col-xl-3 col-lg-4 col-md-6 pb-2">
                            <InputFieldWithValidation label={formMessages[FORM_LABEL_AUTHORITY_NUMBER]}
                                                      field={FORM_FIELD_AUTHORITY_NUMBER}
                                                      isSmall
                                                      maxLength={10}
                                                      isDisabled={!userRights.canEditAuthReply || readOnly || authorityGiven === "NO" || (lastDealerCarRequest && section === FORM_WAREHOUSE_SECTION && lastDealerCarRequest.loanExtension) }
                                                      isRequired={isRequired}/>
                        </div>
                        <div className="col align-self-end">
                            <label className="pl-2 pt-1">
                                <Field
                                    name={FORM_FIELD_AGREEMENT}
                                    component="input"
                                    type="checkbox"
                                    disabled={!userRights.canEditAuthReply || readOnly || authorityGiven === "NO" || (lastDealerCarRequest && section === FORM_WAREHOUSE_SECTION && lastDealerCarRequest.loanExtension) }
                                />{' '}
                                <ValidFormattedMessage message={FORM_LABEL_AGREEMENT}
                                                        intlMessages={formMessages}/>
                            </label>
                        </div>
                    </div>
                    <div className="form-row">
                        {/* {group === 'IT' && ( */}
                            <div className="col-xl-3 col-lg-4 col-md-6 pb-2">
                                <InputFieldWithValidation label={formMessages[FORM_LABEL_APPLICATION_FEES]}
                                                            field={FORM_FIELD_APPLICATION_FEES}
                                                            type="number"
                                                            isSmall
                                                            validations={[positiveNumberWithZero]}
                                                            isDisabled={!userRights.canEditAuthReply || readOnly || authorityGiven === "NO" || (lastDealerCarRequest && section === FORM_WAREHOUSE_SECTION && lastDealerCarRequest.loanExtension) }
                                                            />
                            </div>
                        {/* )} */}
                    </div>
                    {(!loanExtension || (loanExtension && !decreasingRate)) && <div className="form-row">
                        <div className="col-12">
                            <label className="pl-2 pt-1">
                                <input
                                    type="checkbox"
                                    checked={loanExtension ? !!decreasingRateExtension : !!decreasingRate}
                                    onChange={(e) => handleDecreasingRateChange(e.target.checked, loanExtension)}
                                    disabled={!userRights.canEditAuthReply || readOnly || authorityGiven === "NO" || (!loanExtension && lastDealerCarRequest && section === FORM_WAREHOUSE_SECTION && lastDealerCarRequest.loanExtension) }
                                />{' '}
                                <ValidFormattedMessage message={FORM_LABEL_DECREASING_RATE}
                                                       intlMessages={formMessages}/>
                            </label>
                        </div>
                    </div>}
                    <div className="form-row ">
                        <div className="col-xl-3 col-lg-4 col-md-6 pb-2">
                            <Field name={FORM_FIELD_FROM} component={DatePickerComponent} selectsStart
                                   label={formMessages[FORM_LABEL_FROM]}
                                   startDate={startDate} endDate={endDate}
                                   minDate={loanExtension ? moment(warehouseUntilDecreasing || warehouseUntil).add(1, 'day').format('YYYY-MM-DD') : undefined}
                                   isDisabled={!userRights.canEditAuthReply || readOnly || authorityGiven === "NO" || loanExtension || (lastDealerCarRequest && section === FORM_WAREHOUSE_SECTION && lastDealerCarRequest.loanExtension) }
                                   isRequired={isRequired}
                                   validate={isRequired ? required : undefined}
                                   parse={(e) => e ? moment(e).format('YYYY-MM-DD') : ""}
                            />
                        </div>
                        <div className="col-xl-3 col-lg-4 col-md-6 pb-2">
                            <Field name={FORM_FIELD_UNTIL} component={DatePickerComponent} selectsEnd
                                   label={formMessages[FORM_LABEL_UNTIL]}
                                   startDate={startDate} endDate={endDate}
                                   minDate={loanExtension ? moment(warehouseUntilDecreasing || warehouseUntil).add(1, 'day').format('YYYY-MM-DD') : undefined}
                                   isDisabled={!userRights.canEditAuthReply || readOnly || authorityGiven === "NO" || (lastDealerCarRequest && section === FORM_WAREHOUSE_SECTION && lastDealerCarRequest.loanExtension) }
                                   isRequired={isRequired}
                                   validate={isRequired ? required : undefined}
                                   parse={(e) => e ? moment(e).format('YYYY-MM-DD') : ""}
                            />
                        </div>
                        <div className="col-xl-3 col-lg-4 col-md-12 pb-2">
                            <InputSelectFieldWithValidation label={formMessages[FORM_LABEL_DAILY_RATE]}
                                                            field={FORM_FIELD_DAILY_RATE}
                                                            options={dailyRateOptions}
                                                            isSmall
                                                            useSelectInput
                                                            intlMessages={formMessages}
                                                            sort={false}
                                                            isDisabled={!userRights.canEditAuthReply || readOnly || authorityGiven === "NO" || (lastDealerCarRequest && section === FORM_WAREHOUSE_SECTION && lastDealerCarRequest.loanExtension) || (dailyRateOptions.length === 0 && !isNaN(dailyRateLevel)) }
                                                            isRequired={isRequired}/>
                        </div>
                    </div>
                    {((decreasingRate && !loanExtension) || decreasingRateExtension) && (
                        <div className="form-row">
                            <div className="col-xl-3 col-lg-4 col-md-6 pb-2">
                                <Field name={FORM_FIELD_FROM_DECREASING} component={DatePickerComponent} selectsStart
                                       label={formMessages[FORM_LABEL_FROM_DECREASING]}
                                       startDate={startDateDecreasing} endDate={endDateDecreasing}
                                       isDisabled
                                       isRequired={isRequired}
                                       validate={isRequired ? required : undefined}
                                       parse={(e) => e ? moment(e).format('YYYY-MM-DD') : ""}
                                />
                            </div>
                            <div className="col-xl-3 col-lg-4 col-md-6 pb-2">
                                <Field name={FORM_FIELD_UNTIL_DECREASING} component={DatePickerComponent} selectsEnd
                                       label={formMessages[FORM_LABEL_UNTIL_DECREASING]}
                                       startDate={startDateDecreasing} endDate={endDateDecreasing}
                                       minDate={decreasingRateExtension ? moment(endDate).add(1, 'day').format('YYYY-MM-DD') : moment(warehouseUntil).add(1, 'day').format('YYYY-MM-DD')}
                                       isDisabled={!userRights.canEditAuthReply || readOnly || authorityGiven === "NO" || (lastDealerCarRequest && section === FORM_WAREHOUSE_SECTION && lastDealerCarRequest.loanExtension) }
                                       isRequired={isRequired}
                                       validate={isRequired ? required : undefined}
                                       parse={(e) => e ? moment(e).format('YYYY-MM-DD') : ""}
                                />
                            </div>
                            <div className="col-xl-3 col-lg-4 col-md-12 pb-2">
                                <InputSelectFieldWithValidation label={formMessages[FORM_LABEL_DAILY_RATE_DECREASING]}
                                                                field={FORM_FIELD_DAILY_RATE_DECREASING}
                                                                options={dailyRateOptions}
                                                                isSmall
                                                                useSelectInput
                                                                intlMessages={formMessages}
                                                                sort={false}
                                                                isDisabled={!userRights.canEditAuthReply || readOnly || authorityGiven === "NO" || (lastDealerCarRequest && section === FORM_WAREHOUSE_SECTION && lastDealerCarRequest.loanExtension) || (dailyRateOptions.length === 0 && !isNaN(dailyRateLevel)) }
                                                                isRequired={isRequired}/>
                            </div>
                        </div>
                    )}
                    <div className="form-row ">
                        <div className="col-xl-3 col-lg-6 col-md-12 mb-2 align-self-end pb-1 pt-3">
                            {loanExtension && <strong>NEW </strong>}
                            <ValidFormattedMessage message={FORM_LABEL_TOTAL_DAYS}
                                                   intlMessages={formMessages}/>{':'}
                            <span className="bg-light ml-1 pl-1 pr-1 border border-dark rounded">
                                    {(numberOfDays + numberOfDaysDecreasing + (loanExtension ? warehouseDaysRef.current : 0)) || 0}
                                </span>
                        </div>
                        <div className="col-xl-3 col-lg-6 col-md-12 mb-2 align-self-end pb-1 pt-3">
                            {loanExtension && <strong>NEW </strong>}
                            <ValidFormattedMessage message={FORM_LABEL_TOTAL_AMOUNT}
                                                   intlMessages={formMessages}/>{':'}
                            <span className="bg-light ml-1 pl-1 pr-1 border border-dark rounded">
                                    {price}
                            </span>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="col-12 pb-2">
                            <TextareaFieldWithValidation label={formMessages[FORM_LABEL_COURTESY_REPLY_REMARKS]}
                                                         field={FORM_FIELD_COURTESY_REPLY_REMARKS}
                                                         rows={3}
                                                         isDisabled={!userRights.canEditAuthReply || readOnly || authorityGiven === "NO" || (lastDealerCarRequest && section === FORM_WAREHOUSE_SECTION && lastDealerCarRequest.loanExtension) }
                                                         />
                        </div>
                    </div>
                </StyledDiv>
            </div>
            }
        </>
    )
}

const mapStateToProps = (state, initialProps) => {
    const section = initialProps.loanExtension ? FORM_LOAN_EXTENSION_SECTION : FORM_WAREHOUSE_SECTION;
    return {
        section,
        dailyRateLevel: formValueSelector(initialProps.form)(state, `${section}.${FORM_FIELD_DAILY_RATE}`),
        dailyRateLevelDecreasing: formValueSelector(initialProps.form)(state, `${section}.${FORM_FIELD_DAILY_RATE_DECREASING}`),
        authorityGiven: formValueSelector(initialProps.form)(state, `${section}.${FORM_FIELD_AUTHORITY_GIVEN}`),
        startDate: formValueSelector(initialProps.form)(state, `${section}.${FORM_FIELD_FROM}`),
        endDate: formValueSelector(initialProps.form)(state, `${section}.${FORM_FIELD_UNTIL}`),
        startDateDecreasing: formValueSelector(initialProps.form)(state, `${section}.${FORM_FIELD_FROM_DECREASING}`),
        endDateDecreasing: formValueSelector(initialProps.form)(state, `${section}.${FORM_FIELD_UNTIL_DECREASING}`),
        applicationFees: formValueSelector(initialProps.form)(state, `${section}.${FORM_FIELD_APPLICATION_FEES}`),
        decreasingRate: formValueSelector(initialProps.form)(state, `${FORM_WAREHOUSE_SECTION}.decreasingRate`),
        decreasingRateExtension: formValueSelector(initialProps.form)(state, `${section}.decreasingRateExtension`),
        warehouseUntil: formValueSelector(initialProps.form)(state, `${FORM_WAREHOUSE_SECTION}.${FORM_FIELD_UNTIL}`),
        warehouseUntilDecreasing: formValueSelector(initialProps.form)(state, `${FORM_WAREHOUSE_SECTION}.${FORM_FIELD_UNTIL_DECREASING}`),
        group: formValueSelector(initialProps.form)(state, `${FORM_TICKET_SECTION}.group`),
        carRequests: formValueSelector(initialProps.form)(state, `${FORM_CAR_REQUEST_SECTION}.${FORM_CAR_REQUEST}`),
        coutesyCarsPricing: state.adminCourtesyCarsPricing.courtesyCarsPricing,
    };
};

const mapDispatchToProps = (dispatch, props) => ({
    change: (name, value) => dispatch(change(props.form, name, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WarehouseSection);
